/**
 * The public error page: what a visitor sees when a public page cannot load
 * (the API or the database down mid-deploy, a lost connection, a page's
 * code that a new release has replaced), in place of the router's own
 * unstyled "Something went wrong!". It is the errorComponent of the site
 * frame (routes/_site.tsx, so every page under it), of Log in
 * (routes/login.tsx) and of the root route (routes/__root.tsx), the last
 * catch for anything no route below handles, such as the signed-in gate's
 * session check with the API down. Signed-in routes with their own error
 * screen keep it.
 *
 * The same frame as the public 404 (PublicNotFound in routes/__root.tsx):
 * the wordmark bar, the page header, one sentence, two ways on. Ink only:
 * red is for money and for irreversible confirmations, and nothing here is
 * either. Its own styles (PublicError.css), so it never depends on a page
 * that failed to load.
 *
 * Try again reruns the page's checks and loaders (router.invalidate), which
 * also clears this page once they pass. When what failed is the page's own
 * code (a release replaced the file it asked for), only a fresh load of the
 * site can fetch the new one, so Try again reloads instead.
 */
import { useRouter, useRouterState, type ErrorComponentProps } from '@tanstack/react-router';
import { Button, PageHeader, Wordmark } from 'pleach-ds';
import { useState } from 'react';
import { useDocumentMeta } from '@/features/site/useDocumentMeta';
import { isApiError } from '@/api/client';
import { useRouterClick } from '@/lib/useRouterClick';
import './PublicError.css';

export const PUBLIC_ERROR_TITLE = "Pleach can't be reached right now";
/** A page that failed in the browser, with the server fine: saying "your
 *  connection" there would send a chef looking for the wrong problem. */
export const PAGE_ERROR_TITLE = 'Something went wrong on this page';

/** A chunk that no longer exists (the browsers' wordings for a failed
 *  dynamic import): only a full reload fetches the new release. */
const STALE_CODE = /dynamically imported module|importing a module script failed|loading (css )?chunk/i;

export function isStaleCode(error: unknown): boolean {
  return error instanceof Error && STALE_CODE.test(error.message);
}

/** The server or the network, rather than the page itself: a failed fetch,
 *  a 5xx from the API, or a release that replaced the page's code. */
export function isUnreachable(error: unknown): boolean {
  if (isStaleCode(error)) return true;
  if (isApiError(error)) return error.status === 0 || error.status >= 500;
  // Chrome: 'Failed to fetch'; Firefox: 'NetworkError when attempting to
  // fetch resource'; Safari: 'Load failed'.
  return error instanceof Error && /failed to fetch|networkerror|network request failed|load failed/i.test(error.message);
}

export function PublicError({ error }: ErrorComponentProps) {
  const router = useRouter();
  const go = useRouterClick();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [retrying, setRetrying] = useState(false);
  const unreachable = isUnreachable(error);
  const title = unreachable ? PUBLIC_ERROR_TITLE : PAGE_ERROR_TITLE;
  useDocumentMeta({ title: 'Error', description: `${title}.`, noindex: true });

  const retry = () => {
    if (retrying) return;
    if (isStaleCode(error)) {
      window.location.reload();
      return;
    }
    setRetrying(true);
    void router.invalidate().finally(() => setRetrying(false));
  };

  return (
    <div className="pub-error">
      <header className="pub-error__bar">
        <a href="/" className="pub-error__home" aria-label="Pleach home" onClick={go('/')}>
          <Wordmark size={22} tone="ink" />
        </a>
      </header>
      <main id="main" tabIndex={-1} className="pub-error__main">
        <PageHeader eyebrow="Error" title={title} />
        <p className="pub-error__body">
          {unreachable
            ? 'It may be your connection, or an update on our side. Give it a moment, then try again.'
            : 'Nothing you entered was lost on our side. Try again, and if it keeps happening, let us know.'}
        </p>
        <div className="pub-error__actions">
          {/* Not disabled while it tries: a disabled button drops the
              keyboard focus. A second press is ignored instead. */}
          <Button variant="primary" onClick={retry} aria-busy={retrying || undefined}>
            {retrying ? 'Trying again…' : 'Try again'}
          </Button>
          {pathname === '/' ? null : (
            <Button variant="secondary" href="/" onClick={go('/')}>
              Go to the home page
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
